import React, { useState, useEffect, useRef, useContext } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import DocumentsUpload from "./documentsUpload";
import { Constants } from "config/constants";
import { ChevronLeftIcon } from "@heroicons/react/solid";
import _merge from "lodash/merge";
import isEqual from "lodash/isEqual";
import {
  Formik,
  Field,
  Form,
  validateYupSchema,
  yupToFormErrors,
  setNestedObjectValues,
} from "formik";
import { handleFilesUpload } from "utils/filesUtils";
import { clearEmpties, getFullName } from "utils";
import Tabs from "components/lib/Shared/Tabs";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import FileUploadForm from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm";
import { getDocumentData } from "components/PLCTABS/CompanyOfficialsTab/FileUploadForm/documentData";
import getValidationSchema from "components/PLCTABS/CompanyOfficialsTab/AssembledCompanyOfficialForms/CompanyOfficialsAuthorizedDirector/getValidationSchema";
import NamesFormEditable from "./NamesFormEditable/NamesFormEditable";
import ResidentialDetails from "./ResidentialDetails";
import getInitialValues from "components/PLCTABS/CompanyOfficialsTab/AssembledCompanyOfficialForms/CompanyOfficialsAuthorizedDirector/getInitialValues";
import IdentificationDetailsForm from "./IdentificationDetails/index";
import { getOfficial, resetOfficial } from "state/slices/companyDashboard";
import ManageAppointmentDate from "./EffectiveDates/AppointmentDate";
import ManageDateOfChange from "./EffectiveDates/DateOfChange";
import Button from "components/lib/Shared/Button";
import { updateCompanyOfficialAsync } from "state/slices/tables/companyOfficialsSlice";
import {
  convertTemplateToPdfAsync,
  uploadTemplateFileAsync,
} from "state/slices/templateFile";
import { handleCompanyMaintenanceFormData } from "utils/transactionRequestHandler";
import ErrorBanner from "components/lib/Shared/ErrorBanner";
import ChangesContext from "context/ChangesContext";
import TransactionEditContext from "context/TransactionEditContext";
import ReactSelectWithFormik from "components/lib/Shared/DropdownSelect";
import Label from "components/lib/Shared/Label";
import SectionDescription from "components/lib/Shared/SectionDescription";
import Verify from "components/lib/Shared/Verify";
import InviteExistingUser from "components/lib/Shared/Verify/InviteExistingUser";
import InviteNewUser from "components/lib/Shared/Verify/InviteNewUser";
import { ManageModuleContext } from "context/ManageCompanyModuleContext";
import UpdateCompanyDetailsContext from "context/UpdateCompanyDetails";
import { getAllInitiateTransactionChangeAsync } from "state/slices/initiateTransactionChange";
import { editBeneficialOwnerAsync } from "state/slices/tables/beneficialOwnersTableSlice";
import ManualTransactionEditContext from "context/ManualTransactionEditContext";
import { generateBatchNumber } from "utils/generateBatchNumber";
import { updateTransaction } from "state/slices/transaction";

const initialTabs = [
  {
    id: "assignOfficials",
    label: "Assign Officials",
  },
  { id: "generalInformation", label: "General information" },
  { id: "residentialDetails", label: "Address Details" },
  { id: "fileUploads", label: "KYC Documents" },
  { id: "changedDocuments", label: "Change Documents" },
];

const ChangeOfParticularsSubRow = ({
  row,
  visibleColumns,
  changeList,
  setChangeList,
  actionType,
  stakeholderOfficial = null,
  boOfficial = null,
  isBeneficialOwner = false,
  isJointShareholder = false,
}) => {
  const isViewOnly = false;
  const currentUser = useSelector((state) => state.userSlice);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [validationSchema, setValidationSchema] = useState(null);
  const [initialValues, setInitialValues] = useState({});
  const [selectedTab, setSelectedTab] = useState(initialTabs[0]);
  const [isFormValid, setIsFormValid] = useState(false);
  const [isIdAssociated, setIsIdAssociated] = useState(null);
  const [changedFields, setChangedFields] = useState({});
  const [filteredTabs, setFilteredTabs] = useState(initialTabs);
  const [nationalityChangeForm, setNationalityChangeForm] = useState(false);
  const [disableForm, setDisableForm] = useState(false);
  const [inviteNewUserWorkflow, setInviteNewUserWorkflow] = useState(false);
  const [isAccessAllowed, setIsAccessAllowed] = useState(false);
  const [isAccessRevoked, setIsAccessRevoked] = useState(false);
  const [inviteExistingUser, setInviteExistingUser] = useState(false);
  const [showIdentificationSection, setShowIdentificationSection] =
    useState(true);
  const [verificationValue, setVerifyingValue] = useState("");

  const formStepErrors = useRef(null);
  const dispatch = useDispatch();
  const { companyId } = useParams();

  const {
    transactionId,
    setTransactionId,
    transactionPayload,
    setTransactionPayload,
  } = useContext(ManageModuleContext);

  const {
    isInUpdateCompanyDetailsContext,
    createdTransactionsIds,
    setCreatedTransactionsIds,
    transactions,
  } = useContext(UpdateCompanyDetailsContext);

  let transactionToEdit = {};
  const {
    transactionToEdit: transactionToEditForTransactionEditContext,
    isInTransactionEditContext,
  } = useContext(TransactionEditContext);

  const {
    transactionToEdit: transactionToEditForManual,
    isInManualTransactionEditContext,
  } = useContext(ManualTransactionEditContext);

  let jointShareholderTransaction = null;
  let childBOtransaction = null;
  let BOexists = false;
  if (transactions && transactions?.length > 0 && row.original?.JSGroupID) {
    jointShareholderTransaction = transactions.find(
      (trx) =>
        trx?.JSGroupID === row.original?.JSGroupID &&
        trx?.applicationType ===
        Constants.APPLICANT_CAPACITIES.JOINT_SHAREHOLDER
    );
    childBOtransaction = transactions.find(
      (trx) => trx?.JSGroupID === row.original?.JSGroupID
    );
  }
  if (childBOtransaction) {
    const childBOs = childBOtransaction?.beneficialOwners;
    if (childBOs?.length > 0) {
      BOexists = childBOs.find(
        (child) =>
          child?.names?.firstName === boOfficial?.names?.firstName &&
          child?.names?.lastName === boOfficial?.names?.lastName
      );
    }
  }

  if (transactionToEditForTransactionEditContext) {
    transactionToEdit = transactionToEditForTransactionEditContext;
  } else if (transactionToEditForManual) {
    transactionToEdit = transactionToEditForManual;
  }

  const { official, getOfficialStatus } = useSelector(
    (state) => state.companyDashboardSlice
  );
  const { batchNo } = useSelector((state) => state.batchNumberSlice);
  const documentData = getDocumentData(row?.original?.idType);

  const goToPrevStep = () => {
    if (selectedTabIndex === 0) return;
    setSelectedTabIndex(selectedTabIndex - 1);
  };

  const isValidFile = (file) => {
    if (!(file instanceof File)) return false;
    if (file.size === 0) return false;
    const allowedTypes = ["application/pdf"];
    return allowedTypes.includes(file.type);
  };

  const isFileChanged = (newFile, oldFile) => {
    if (newFile instanceof File && isValidFile(newFile)) return true;
    if (newFile === null && oldFile) return true;
    if (newFile && !oldFile) return true;
    if (typeof newFile === "string" && newFile !== oldFile) return true;
    return false;
  };

  useEffect(() => {
    if (row?.original?._id) {
      dispatch(
        getOfficial({
          companyId,
          officialId: row?.original?._id,
          queryParams: {
            idType: row?.original?.idType,
            JSGroupID: row?.original?.JSGroupID,
          }
        })
      );
    }
    return () => {
      dispatch(resetOfficial());
    };
  }, [row?.original?._id]);

  useEffect(() => {
    const tempSelectedTab = filteredTabs[selectedTabIndex];
    if (tempSelectedTab) setSelectedTab(tempSelectedTab);
  }, [selectedTabIndex, filteredTabs]);

  // Initialize form values with official data and override with changed fields from changeList
  useEffect(() => {
    if (!official) return;

    let formInitialValues = _merge(
      getInitialValues({
        idType: row?.original?.idType,
        isNonResident: row?.original?.isNonResident,
        documentData,
      }),
      official
    );

    // Find official in changeList and merge changedFields (excluding fileUploads and uploadChangeDocument)
    const officialInChangeList = changeList.find(
      (item) => item._id === row?.original?._id
    );
    if (officialInChangeList?.changedFields) {
      const { fileUploads, uploadChangeDocument, ...otherChangedFields } =
        officialInChangeList.changedFields;
      formInitialValues = _merge(formInitialValues, otherChangedFields);
    }
    if (official?.changedFields) {
      const { fileUploads, uploadChangeDocument, ...otherChangedFields } =
        official.changedFields;
      formInitialValues = _merge(formInitialValues, otherChangedFields);
    }

    setInitialValues(formInitialValues);
  }, [row?.original?.idType, official, documentData, changeList]);

  if (!official || getOfficialStatus.status === "loading") {
    return (
      <tr className="bg-[#EDF0F5]">
        <td colSpan={visibleColumns.length} className="px-9">
          <div className="flex justify-center mx-auto m-2">
            <SpinnerIcon className="text-gray-400" />
          </div>
        </td>
      </tr>
    );
  }

  const isFirstStep = selectedTabIndex === 0;
  const handleNewUserWorkflow = () => {
    setInviteNewUserWorkflow(true);
  };

  const createUploadFilePayload = (companyId, payload, officialId) => {
    let fileName;
    let modeType;
    let subModeType;
    let fieldKey;

    if (payload?.uploadedFiles?.deedPoll) {
      fileName = payload.uploadedFiles.deedPoll;
      modeType = "pre-signed-documents";
      subModeType = "self-resolution-type";
      fieldKey = "deedPoll";
    } else if (payload?.uploadedFiles?.certificateOfChangeOfName) {
      fileName = payload.uploadedFiles.certificateOfChangeOfName;
      modeType = "pre-signed-documents";
      subModeType = "self-resolution-type";
      fieldKey = "certificateOfChangeOfName";
    } else if (payload?.uploadedFiles?.statutoryDeclaration) {
      fileName = payload.uploadedFiles.statutoryDeclaration;
      modeType = "pre-signed-documents";
      subModeType = "self-resolution-type";
      fieldKey = "statutoryDeclaration";
    } else if (payload?.getStatutoryDeclarationName) {
      fileName = payload.getStatutoryDeclarationName;
      modeType = "not-signed-documents";
      subModeType = "platform-resolution-type";
      fieldKey = "statutoryDeclaration";
    }

    return {
      company: companyId,
      official: officialId,
      fileType: Constants.Template_FILE_TYPES.CONSENT_TO_ACT,
      fileName: fileName,
      modeType: modeType,
      subModeType: subModeType,
      fieldKey,
    };
  };

  const createUploadChangeDocumentTemplate = async (uploadFilePayload) => {
    const fileUploadResponse = await dispatch(
      uploadTemplateFileAsync({ data: uploadFilePayload })
    );
    const uploadChangeDocumentTemplate = fileUploadResponse.payload.document;
    return uploadChangeDocumentTemplate;
  };

  const createUploadChangeDocumentPdf = async (uploadChangeDocument) => {
    let uploadChangePdfDocument = "";
    if (uploadChangeDocument.modeType === "not-signed-documents") {
      const convertToPdfResponse = await dispatch(
        convertTemplateToPdfAsync({
          data: uploadChangeDocument,
        })
      );
      uploadChangePdfDocument = convertToPdfResponse.payload.fileName;
    }
    return uploadChangeDocument;
  };

  const createUploadChangeDocsForOfficial = async (
    companyId,
    payload,
    official
  ) => {
    const uploadFilePayload = createUploadFilePayload(
      companyId,
      payload,
      official._id
    );
    let updatedOfficial = {};
    if (uploadFilePayload.fileName && uploadFilePayload.modeType) {
      if (official) {
        const uploadChangeDocument = await createUploadChangeDocumentTemplate(
          uploadFilePayload
        );

        const uploadChangeDocumentPdf = await createUploadChangeDocumentPdf(
          uploadChangeDocument
        );

        const officialUpdateData = {
          changeOfParticularDate: payload?.changeOfParticularDate,
          uploadChangeDocuments: {
            ...row?.original?.uploadChangeDocuments,
            changeOfParticulars: {
              ...row?.original?.uploadChangeDocuments?.changeOfParticulars,
              [uploadFilePayload.fieldKey]: {
                convertedPdf: uploadChangeDocumentPdf,
                templateFile: uploadChangeDocument,
              },
            },
          },
          changeType: Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_PARTICULARS,
          companyId,
          idType: row?.original?.idType,
        };

        let response = {};
        if (isBeneficialOwner) {
          // Update BO on the BOs table
          response = await dispatch(
            editBeneficialOwnerAsync({
              companyId,
              beneficialOwnerId: row?.original?._id,
              data: officialUpdateData,
            })
          );

          // Update details of the company official in the stakeholder table who also appears in the B.O dropdown
          if (stakeholderOfficial && stakeholderOfficial?._id) {
            await dispatch(
              updateCompanyOfficialAsync({
                officialId: stakeholderOfficial?._id,
                data: officialUpdateData,
              })
            );
          }
        } else {
          response = await dispatch(
            updateCompanyOfficialAsync({
              officialId: official?._id,
              data: officialUpdateData,
            })
          );

          //update details of BO who is linked to this individual
          if (boOfficial && boOfficial?._id) {
            await dispatch(
              editBeneficialOwnerAsync({
                companyId,
                beneficialOwnerId: boOfficial._id,
                data: officialUpdateData,
              })
            );
          }
        }

        updatedOfficial = response?.payload?.official;
      }
    }
    return updatedOfficial;
  };

  const identificationDetailsKeys = [
    "dateOfBirth",
    "nationalIDNumber",
    "emailAddress",
    "mobileNumber",
    "buildingName",
    "KRAPin",
    "entityType",
    "occupation",
    "gender",
    "guardianFullName",
    "guardianRelationship",
    "guardianMobileNumber",
    "guardianEmailAddress",
    "nationality",
    "passportNumber",
    "foreignCertificateID",
    "foreignCertificateDateOfExpiry",
    "secretaryNumber",
    "practicingNumber",
    "passportDateOfExpiry",
    "birthCertificateNumber",
    "companyContactDesignation",
    "companyContactName",
    "companyFormerName",
    "companyKRAPIN",
    "telNumber",
    "governingLaw",
    "countryOfIncorporation",
    "companyIncorporationNumber",
    "appointmentDate",
  ];

  const namesKeys = [
    "firstName",
    "middleName",
    "lastName",
    "fullName",
    "companyName",
    "companyRegNumber",
    "dateOfRegistration",
    "formerName",
  ];

  const residentialAddressDetailsKeys = [
    "buildingName",
    "houseNumber",
    "floorNumber",
    "estate",
    "streetName",
    "streetNumber",
    "city",
    "locality",
    "district",
    "county",
    "country",
    "nationality",
  ];

  const electronicAddressKeys = ["emailAddress", "mobileNumber"];

  const postalAddressKeys = ["postalAddress", "postalCode", "serviceAddress"];

  const fileUploadsKeys = [
    "passportPhoto",
    "passportBIOS",
    "workPermit",
    "KRACert",
    "foreignerCert",
    "practicingCert",
    "Choose File",
    "nationalID",
    "certOfIncorporation",
    "certOfIncumbency",
    "leadPartnerCertificate",
    "articlesOfAssociation",
    "searchResults",
    "birthCertificate",
  ];

  return (
    <tr className="bg-[#EDF0F5]">
      <td colSpan={12} className={`px-9`}>
        <Formik
          enableReinitialize
          initialValues={{
            role: [],
            ...initialValues,
            changeDocumentsType: "",
            consentFileForAuthorizedPerson: null,
            consentResolutionType: "",
            consentResolutionTypeFile: null,
            consentSflResolutionTypeFile: null,
          }}
          validate={(values) => {
            try {
              validateYupSchema(values, validationSchema, true, {
                makeFieldsOptional: isIdAssociated === false || isViewOnly,
              });
            } catch (err) {
              const errors = yupToFormErrors(err);
              if (nationalityChangeForm && !values.idType) {
                errors.idType = "ID Type is required after nationality change";
              }
              return errors;
            }
          }}
          onSubmit={async (values, { resetForm }) => {
            resetForm();
          }}
        >
          {({
            values,
            errors,
            touched,
            isSubmitting,
            setFieldValue,
            resetForm,
            setTouched,
            submitCount,
            setFieldError,
            setSubmitting,
            setErrors,
          }) => {
            const submitForm = async () => {
              setSubmitting(true);
              try {
                // Check if only changeOfParticularDate is changed
                const hasOtherChanges = Object.keys(changedFields).some(
                  (key) => key !== "changeOfParticularDate"
                );

                if (
                  (changedFields.changeOfParticularDate && !hasOtherChanges) ||
                  !hasOtherChanges
                ) {
                  setFieldError(
                    "form",
                    "No changes of particulars have been made to the form."
                  );
                  setSubmitting(false);
                  return;
                }

                let documentErrors = [];
                if (changedFields.names) {
                  const hasNameChangeDocument =
                    isValidFile(values.changeDocumentsType?.deedPoll) ||
                    isValidFile(
                      values.changeDocumentsType?.certificateOfChangeOfName
                    );
                  if (!hasNameChangeDocument) {
                    documentErrors.push(
                      "A Deed Poll or Certificate of Change of Name is required for name changes."
                    );
                  }
                }

                if (
                  changedFields.electronicAddress &&
                  Constants.INDIVIDUALS_ID_TYPES.includes(row.original.idType)
                ) {
                  const hasStatutoryDeclaration =
                    isValidFile(
                      values.statutoryDeclaration?.statutoryDeclaration
                    ) ||
                    Boolean(
                      values.statutoryDeclaration?.getStatutoryDeclarationName
                    );
                  if (!hasStatutoryDeclaration) {
                    documentErrors.push(
                      "A Statutory Declaration is required for electronic address changes."
                    );
                  }
                }

                if (documentErrors.length > 0) {
                  setFieldError("form", documentErrors);
                  setSubmitting(false);
                  return;
                }

                if (Object.keys(errors).length > 0) {
                  setSubmitting(false);
                  return;
                }
                const documentsToUpload = {};

                if (changedFields.names) {
                  if (values.changeDocumentsType?.deedPoll) {
                    documentsToUpload.deedPoll =
                      values.changeDocumentsType.deedPoll;
                  } else if (
                    values.changeDocumentsType?.certificateOfChangeOfName
                  ) {
                    documentsToUpload.certificateOfChangeOfName =
                      values.changeDocumentsType.certificateOfChangeOfName;
                  }
                }

                if (changedFields.electronicAddress) {
                  if (values.statutoryDeclaration?.statutoryDeclaration) {
                    documentsToUpload.statutoryDeclaration =
                      values.statutoryDeclaration.statutoryDeclaration;
                  }
                }

                let uploadedFiles = null;

                if (Object.keys(documentsToUpload).length > 0) {
                  uploadedFiles = await handleFilesUpload(
                    documentsToUpload,
                    official
                  );
                }
                let payload = {
                  uploadedFiles,
                };
                if (values?.statutoryDeclaration?.getStatutoryDeclarationName) {
                  payload = {
                    ...payload,
                    getStatutoryDeclarationName:
                      values?.statutoryDeclaration
                        ?.getStatutoryDeclarationName || "",
                  };
                }
                let updatedOfficial = {};

                // Update official with changed fields
                if (changedFields && Object.keys(changedFields).length > 0) {
                  if (isBeneficialOwner) {
                    updatedOfficial = await dispatch(
                      editBeneficialOwnerAsync({
                        companyId,
                        beneficialOwnerId: row?.original?._id,
                        data: {
                          changedFields: { ...changedFields },
                          companyId,
                          idType: row?.original?.idType,
                        },
                      })
                    );
                  } else {
                    updatedOfficial = await dispatch(
                      updateCompanyOfficialAsync({
                        officialId: row?.original?._id,
                        data: {
                          changedFields: { ...changedFields },
                          companyId,
                          idType: row?.original?.idType,
                        },
                      })
                    );
                  }
                }

                if (payload?.uploadedFiles?.deedPoll) {
                  updatedOfficial = await createUploadChangeDocsForOfficial(
                    companyId,
                    {
                      uploadedFiles: {
                        deedPoll: payload.uploadedFiles.deedPoll,
                      },
                      changeOfParticularDate: values?.changeOfParticularDate,
                    },
                    updatedOfficial?._id ? updatedOfficial : row?.original
                  );
                }
                if (payload?.uploadedFiles?.certificateOfChangeOfName) {
                  updatedOfficial = await createUploadChangeDocsForOfficial(
                    companyId,
                    {
                      uploadedFiles: {
                        certificateOfChangeOfName:
                          payload.uploadedFiles.certificateOfChangeOfName,
                      },
                      changeOfParticularDate: values?.changeOfParticularDate,
                    },
                    updatedOfficial?._id ? updatedOfficial : row?.original
                  );
                }
                if (payload?.uploadedFiles?.statutoryDeclaration) {
                  updatedOfficial = await createUploadChangeDocsForOfficial(
                    companyId,
                    {
                      uploadedFiles: {
                        statutoryDeclaration:
                          payload.uploadedFiles.statutoryDeclaration,
                      },
                      changeOfParticularDate: values?.changeOfParticularDate,
                    },
                    updatedOfficial?._id ? updatedOfficial : row?.original
                  );
                }
                if (payload?.getStatutoryDeclarationName) {
                  updatedOfficial = await createUploadChangeDocsForOfficial(
                    companyId,
                    {
                      getStatutoryDeclarationName:
                        payload.getStatutoryDeclarationName,
                      changeOfParticularDate: values?.changeOfParticularDate,
                    },
                    updatedOfficial?._id ? updatedOfficial : row?.original
                  );
                }

                let updatedPayload = { ...transactionPayload };

                const isIndividual = Constants.INDIVIDUALS_ID_TYPES.includes(
                  values.idType
                );

                if (
                  (isInTransactionEditContext && transactionToEdit?._id) ||
                  (isInManualTransactionEditContext &&
                    transactionToEdit?.changeType ===
                    Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_PARTICULARS)
                ) {
                  updatedPayload = {
                    ...transactionToEdit,
                    [isBeneficialOwner
                      ? "beneficialOwners"
                      : isIndividual
                        ? "individualOfficials"
                        : "corporateOfficials"]: [
                        ...(transactionToEdit[
                          isBeneficialOwner
                            ? "beneficialOwners"
                            : isIndividual
                              ? "individualOfficials"
                              : "corporateOfficials"
                        ]
                          ?.filter(
                            (official) => official?._id !== row?.original._id
                          )
                          ?.map((official) => official._id || official) || []),
                        row?.original._id,
                      ],
                    changeOfParticularDate:
                      values.changeOfParticularDate ||
                      transactionToEdit.changeOfParticularDate,
                    companyId,
                    documentId: transactionToEdit._id,
                    ...(batchNo && { batchNo }),
                  };
                  await handleCompanyMaintenanceFormData(
                    "existing-request",
                    updatedPayload,
                    companyId
                  );
                } else if (jointShareholderTransaction) {
                  const jointUpdate = {
                    ...jointShareholderTransaction,
                    ["individualOfficials"]: [
                      ...(jointShareholderTransaction[
                        "individualOfficials"
                      ]?.map((official) => official._id || official) || []),
                      row?.original._id,
                    ],
                    changeOfParticularDate:
                      values.changeOfParticularDate ||
                      jointShareholderTransaction.changeOfParticularDate,
                    companyId,
                    documentId: jointShareholderTransaction._id,
                    ...(batchNo && { batchNo }),
                  };

                  await handleCompanyMaintenanceFormData(
                    "existing-request",
                    jointUpdate,
                    companyId
                  );

                  if (!BOexists) {
                    const boOfficialupdatedPayload = {
                      ...transactionPayload,
                      changeOfParticularDate: values.changeOfParticularDate,
                      createdBy: currentUser._id,
                      changeType:
                        Constants.TRANSACTION_CHANGE_TYPES
                          .CHANGE_OF_PARTICULARS,
                      companyId,
                      ...(batchNo && { batchNo }),
                      transactionType: isInUpdateCompanyDetailsContext
                        ? Constants.TRANSACTION_TYPES.MANUAL
                        : Constants?.TRANSACTION_TYPES.AUTOMATIC,
                      JSGroupID: row.original?.JSGroupID,
                      isBeneficialOwner: true,
                      officialId: boOfficial._id,
                      idType: boOfficial?.idType,
                      applicationType:
                        Constants.APPLICANT_CAPACITIES.BENEFICIAL_OWNER,
                      linkedTransaction: transactionToEdit._id, // Link to BO transaction
                    };
                    const boTrxRes = await handleCompanyMaintenanceFormData(
                      "new-request",
                      boOfficialupdatedPayload,
                      companyId
                    );

                    dispatch(
                      updateTransaction({
                        trxId: jointShareholderTransaction._id,
                        data: {
                          linkedChildTransactions: [
                            ...jointShareholderTransaction.linkedChildTransactions,
                            boTrxRes?.payload?.savedTransaction?._id,
                          ],
                        },
                      })
                    );

                    // This automatically fetches transactions
                    setCreatedTransactionsIds([
                      ...createdTransactionsIds,
                      ...boTrxRes?.payload?.savedTransaction?._id,
                    ]);
                  }
                } else if (!transactionId && !isInTransactionEditContext) {
                  updatedPayload = {
                    ...transactionPayload,
                    officialId: row?.original?._id,
                    changeOfParticularDate: values.changeOfParticularDate,
                    applicationType: isBeneficialOwner
                      ? Constants.APPLICANT_CAPACITIES.BENEFICIAL_OWNER
                      : values?.designation,
                    createdBy: currentUser._id,
                    idType: values.idType,
                    isBeneficialOwner,
                    changeType:
                      Constants.TRANSACTION_CHANGE_TYPES.CHANGE_OF_PARTICULARS,
                    companyId,
                    ...(batchNo && { batchNo }),
                    transactionType: isInUpdateCompanyDetailsContext
                      ? Constants.TRANSACTION_TYPES.MANUAL
                      : Constants?.TRANSACTION_TYPES.AUTOMATIC,
                    JSGroupID: row.original?.JSGroupID,
                  };

                  let createdTrxIds = [];

                  const transactionResponse =
                    await handleCompanyMaintenanceFormData(
                      "new-request",
                      updatedPayload,
                      companyId
                    );

                  const newTrxId =
                    transactionResponse?.payload?.savedTransaction?._id;

                  createdTrxIds.push(newTrxId);

                  if (newTrxId && !isInUpdateCompanyDetailsContext) {
                    setTransactionId(newTrxId);
                    updatedPayload.trx = newTrxId;
                  }

                  // Create a transaction for stakeholder who is a BO
                  if (stakeholderOfficial && stakeholderOfficial?._id) {
                    const stakeholderPayload = {
                      ...updatedPayload,
                      officialId: stakeholderOfficial._id,
                      idType: stakeholderOfficial?.idType,
                      applicationType: stakeholderOfficial?.designation,
                      linkedTransaction: newTrxId, // Link to BO transaction
                    };
                    const stakeholderTrxRes =
                      await handleCompanyMaintenanceFormData(
                        "new-request",
                        stakeholderPayload,
                        companyId
                      );

                    const stakeholderTrxId =
                      stakeholderTrxRes?.payload?.savedTransaction?._id;

                    if (stakeholderTrxId) {
                      createdTrxIds.push(stakeholderTrxId);
                    }

                    dispatch(
                      updateTransaction({
                        trxId: newTrxId,
                        data: {
                          linkedChildTransactions: [stakeholderTrxId],
                        },
                      })
                    );
                  }

                  // create transaction for a BO
                  if (boOfficial && boOfficial?._id) {
                    const boOfficialupdatedPayload = {
                      ...updatedPayload,
                      isBeneficialOwner: true,
                      officialId: boOfficial._id,
                      idType: boOfficial?.idType,
                      applicationType:
                        Constants.APPLICANT_CAPACITIES.BENEFICIAL_OWNER,
                      linkedTransaction: newTrxId, // Link to BO transaction
                    };
                    const boTrxRes = await handleCompanyMaintenanceFormData(
                      "new-request",
                      boOfficialupdatedPayload,
                      companyId
                    );

                    const boTrxId = boTrxRes?.payload?.savedTransaction?._id;

                    if (boTrxId) {
                      createdTrxIds.push(boTrxId);
                    }

                    dispatch(
                      updateTransaction({
                        trxId: newTrxId,
                        data: {
                          linkedChildTransactions: [boTrxId],
                        },
                      })
                    );
                  }

                  if (isInUpdateCompanyDetailsContext) {
                    setCreatedTransactionsIds([
                      ...createdTransactionsIds,
                      ...createdTrxIds,
                    ]);
                  }
                } else {
                  updatedPayload = {
                    ...transactionPayload,
                    [isBeneficialOwner
                      ? "beneficialOwners"
                      : isIndividual
                        ? "individualOfficials"
                        : "corporateOfficials"]: [
                        ...(transactionPayload[
                          isBeneficialOwner
                            ? "beneficialOwners"
                            : isIndividual
                              ? "individualOfficials"
                              : "corporateOfficials"
                        ] || []),
                        row.original._id,
                      ],
                    documentId: transactionId,
                    ...(batchNo && { batchNo }),
                  };
                  let response = await handleCompanyMaintenanceFormData(
                    "existing-request",
                    updatedPayload,
                    companyId
                  );
                }
                if (!isInUpdateCompanyDetailsContext) {
                  dispatch(
                    getAllInitiateTransactionChangeAsync({
                      companyId,
                      params: {
                        status:
                          Constants.MAINTAIN_COMPANY_REVIEW_STATUSES.DRAFT,
                      },
                    })
                  );
                }

                if (!isInTransactionEditContext) {
                  setTransactionPayload(updatedPayload);
                }

                // Update changeList with official and changedFields
                if (actionType !== "edit") {
                  setChangeList((prev) => [
                    ...prev.filter((item) => item._id !== row.original._id), // Remove existing entry if present
                    { ...row.original, changedFields },
                  ]);
                }
                row.toggleRowExpanded();
              } catch (error) {
                setFieldError("form", "An error occurred during submission.");
                setSubmitting(false);
              }
            };

            useEffect(() => {
              const changes = {};
              const isMeaningfulChange = (newValue, oldValue) => {
                return (
                  (Boolean(newValue) && newValue !== oldValue) ||
                  (!newValue && Boolean(oldValue))
                );
              };

              // Names changes
              const namesChanges = {};

              namesKeys.forEach((key) => {
                if (
                  isMeaningfulChange(
                    values.names?.[key],
                    official?.names?.[key]
                  )
                ) {
                  namesChanges[key] = values.names?.[key];
                }
              });

              if (Object.keys(namesChanges).length > 0) {
                changes.names = namesChanges;
              }

              // Identification details changes
              const identificationDetailsChanges = {};

              identificationDetailsKeys.forEach((key) => {
                if (
                  isMeaningfulChange(
                    values.identificationDetails?.[key],
                    official?.identificationDetails?.[key]
                  )
                ) {
                  identificationDetailsChanges[key] =
                    values.identificationDetails?.[key];
                }
              });

              if (Object.keys(identificationDetailsChanges).length > 0) {
                changes.identificationDetails = identificationDetailsChanges;
              }

              // Electronic address changes
              const electronicAddressChanges = {};
              electronicAddressKeys.forEach((key) => {
                if (
                  isMeaningfulChange(
                    values.electronicAddress?.[key],
                    official?.electronicAddress?.[key]
                  )
                ) {
                  electronicAddressChanges[key] =
                    values.electronicAddress?.[key];
                }
              });

              if (Object.keys(electronicAddressChanges).length > 0) {
                changes.electronicAddress = electronicAddressChanges;
              }

              // Postal address changes
              const postalAddressChanges = {};
              postalAddressKeys.forEach((key) => {
                if (
                  isMeaningfulChange(
                    values.postalAddress?.[key],
                    official?.postalAddress?.[key]
                  )
                ) {
                  postalAddressChanges[key] = values.postalAddress?.[key];
                }
              });

              if (Object.keys(postalAddressChanges).length > 0) {
                changes.postalAddress = postalAddressChanges;
              }

              // Residential address details changes
              const residentialAddressDetailsChanges = {};
              residentialAddressDetailsKeys.forEach((key) => {
                if (
                  isMeaningfulChange(
                    values.residentialAddressDetails?.[key],
                    official?.residentialAddressDetails?.[key]
                  )
                ) {
                  residentialAddressDetailsChanges[key] =
                    values.residentialAddressDetails?.[key];
                }
              });

              if (Object.keys(residentialAddressDetailsChanges).length > 0) {
                changes.residentialAddressDetails =
                  residentialAddressDetailsChanges;
              }

              // Cessation date changes
              if (
                isMeaningfulChange(
                  values.changeOfParticularDate,
                  official?.changeOfParticularDate
                )
              ) {
                changes.changeOfParticularDate = values.changeOfParticularDate;
              }

              // File uploads changes
              const fileUploadsChanges = {};
              fileUploadsKeys.forEach((key) => {
                if (
                  isFileChanged(
                    values.fileUploads?.[key],
                    official?.fileUploads?.[key]
                  )
                ) {
                  fileUploadsChanges[key] = values.fileUploads?.[key];
                }
              });

              if (Object.keys(fileUploadsChanges).length > 0) {
                changes.fileUploads = fileUploadsChanges;
              }

              setChangedFields(changes);
            }, [
              values.names,
              values.identificationDetails,
              values.electronicAddress,
              values.postalAddress,
              values.residentialAddressDetails,
              values.changeOfParticularDate,
              values.fileUploads,
              official,
            ]);

            const isContactPerson =
              [
                values?.electronicAddress?.emailAddress,
                values?.identificationDetails?.emailAddress,
              ].includes(currentUser.electronicAddress.emailAddress) &&
              values?.type === "invited";

            useEffect(() => {
              if (isContactPerson) {
                setFieldValue(
                  "identificationDetails.companyContactName",
                  getFullName(currentUser?.names)
                );
                setFieldValue(
                  "identificationDetails.telNumber",
                  currentUser?.electronicAddress?.mobileNumber ?? ""
                );
              }
            }, [isContactPerson]);

            useEffect(() => {
              setValidationSchema(
                getValidationSchema({
                  selectedTab,
                  idType: row.original.idType,
                  isNonResident: values.isNonResident,
                  documentData,
                  isInChangeOfParticulars: true,
                  nationalityChangeForm,
                }).schema
              );
            }, [
              selectedTab,
              row.original.idType,
              values.isNonResident,
              documentData,
            ]);

            useEffect(() => {
              resetForm();
              formStepErrors.current = null;
            }, [row.original.idType]);

            useEffect(() => {
              if (formStepErrors.current?.[selectedTab.id]?.length > 0) {
                try {
                  validateYupSchema(values, validationSchema, true);
                  formStepErrors.current = {
                    ...formStepErrors.current,
                    [selectedTab.id]: [],
                  };
                } catch (error) {
                  formStepErrors.current = {
                    ...formStepErrors.current,
                    [selectedTab.id]: error.errors,
                  };
                }
              }
            }, [values]);

            useEffect(() => {
              if (
                values.isIncompleteProfile &&
                Object.keys(errors).length > 0 &&
                submitCount > 0
              ) {
                setTouched(setNestedObjectValues(errors, true));
              }
            }, [values.isIncompleteProfile, errors, submitCount]);

            useEffect(() => {
              const shouldIncludeChangedDocs =
                changedFields?.names &&
                  Object.values(changedFields.names || {}).some(Boolean)
                  ? true
                  : changedFields?.electronicAddress &&
                    Object.values(changedFields.electronicAddress || {}).some(
                      Boolean
                    ) &&
                    Constants.INDIVIDUALS_ID_TYPES.includes(values.idType)
                    ? true
                    : false;

              const newTabs = initialTabs.filter((tab) => {
                if (tab.id === "changedDocuments")
                  return shouldIncludeChangedDocs;
                if (tab.id === "assignOfficials") return nationalityChangeForm;
                return true;
              });
              setFilteredTabs(newTabs);
            }, [
              changedFields.names,
              changedFields.electronicAddress,
              changedFields.identificationDetails,
              nationalityChangeForm,
            ]);

            useEffect(() => {
              if (nationalityChangeForm) {
                setSelectedTabIndex(0);
              }
            }, [nationalityChangeForm]);

            const handleSelect = (name) => (option) => {
              if (name === "designation") {
                setFieldValue("idType", "");
              }
              setFieldValue(name, option.value);
              setFieldValue("isDirector", "No");
            };

            const getIdTypeOptions = () => {
              return [
                { value: "Kenyan Citizen", label: "Kenyan Citizen" },
                { value: "Foreign Resident", label: "Foreign Resident" },
                { value: "Foreigner", label: "Foreigner" },
              ].filter((option) => option.value !== row.original.idType);
            };

            const isLastStep = filteredTabs.length - 1 === selectedTabIndex;
            const goToNextStep = () => {
              if (
                nationalityChangeForm &&
                (!values.idType || values.idType === row.original.idType) &&
                selectedTab.id === "assignOfficials"
              ) {
                setFieldError(
                  "idType",
                  "Please select an ID Type before proceeding."
                );
                return;
              }
              setSelectedTabIndex(selectedTabIndex + 1);
            };
            // Invite new user form
            if (inviteNewUserWorkflow && selectedTab.id !== "assignOfficials") {
              return (
                <InviteNewUser
                // closeVerifyProcess={closeVerifyProcess}
                // idType={idType}
                // designation={designation}
                // values={values}
                // inviteOfficial={addOrEditOfficial}
                />
              );
            }

            if (inviteExistingUser) {
              return (
                <InviteExistingUser
                // userId={values.userId}
                // idType={idType}
                // designation={designation}
                // closeVerifyProcess={closeVerifyProcess}
                // userEmailAddress={values.userEmailAddress}
                // values={values}
                // inviteOfficial={addOrEditOfficial}
                />
              );
            }
            return (
              <Form>
                <div className="relative">
                  <Tabs
                    tabs={filteredTabs}
                    selectedTabIndex={selectedTabIndex}
                    setSelectedTabIndex={
                      (nationalityChangeForm &&
                        values.idType === row.original.idType) ||
                        values.idType === null
                        ? () => null
                        : setSelectedTabIndex
                    }
                  />
                  {verificationValue &&
                    isIdAssociated === false &&
                    selectedTab.id &&
                    !["assignOfficials", "generalInformation"].includes(
                      selectedTab.id
                    ) && (
                      <button
                        type="button"
                        onClick={handleNewUserWorkflow}
                        className="inline-flex self-end justify-center px-4 py-2 mt-4 text-sm font-medium text-white border border-transparent rounded-md shadow-sm w-content bg-sflPrimary hover:bg-optional hover:text-sflPrimary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:bg-optional disabled:hover:text-white"
                      >
                        Invite
                      </button>
                    )}
                  {selectedTab.id === "assignOfficials" &&
                    nationalityChangeForm && (
                      <div>
                        <div className="px-4 py-6 mt-2 bg-white rounded-sm shadow-sm sm:p-6 sm:rounded md:rounded-md">
                          <div className="grid grid-cols-6 gap-6">
                            <div className="col-span-6 sm:col-span-3">
                              <Label htmlFor="designation">
                                Company Officials
                              </Label>
                              <Field
                                name="designation"
                                component={ReactSelectWithFormik}
                                className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                id="designation"
                                type="text"
                                placeholder={
                                  values?.designation
                                    ? values.designation
                                    : "Select designation"
                                }
                                isDisabled={true}
                                value={
                                  values?.designation ? values.designation : ""
                                }
                              />
                            </div>
                            <div className="col-span-6 sm:col-span-3">
                              <Label htmlFor="idType">ID Types</Label>
                              <Field
                                name="idType"
                                component={ReactSelectWithFormik}
                                options={getIdTypeOptions()}
                                className="block w-full px-3 py-2 mt-1 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                id="idType"
                                placeholder="Select Id type"
                                onChange={handleSelect("idType")}
                                value={values?.idType ? values?.idType : ""}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  {selectedTab.id === "generalInformation" && (
                    <div className="bg-white rounded-sm shadow-sm sm:rounded md:rounded-md">
                      <div className="px-4 py-6 space-y-6 sm:p-6">
                        <div className="mb-4">
                          <h3 className="text-lg font-medium leading-6 text-gray-900">
                            Personal Information
                          </h3>
                          {nationalityChangeForm && (
                            <SectionDescription description="To activate the form fields please verify your ID first" />
                          )}
                        </div>
                        {nationalityChangeForm && values.idType && (
                          <Verify
                            idType={values?.idType}
                            designation={values.designation}
                            setShowIdentificationSection={
                              setShowIdentificationSection
                            }
                            verificationValue={verificationValue}
                            isIdAssociated={isIdAssociated}
                            isAccessAllowed={isAccessAllowed}
                            isAccessRevoked={isAccessRevoked}
                            setFieldValue={setFieldValue}
                            setVerifyingValue={setVerifyingValue}
                            setIsIdAssociated={setIsIdAssociated}
                            setDisableForm={setDisableForm}
                            handleNewUserWorkflow={handleNewUserWorkflow}
                            setIsAccessAllowed={setIsAccessAllowed}
                            setIsAccessRevoked={setIsAccessRevoked}
                            setInviteExistingUser={setInviteExistingUser}
                          //setTabsClickable={setTabsClickable}
                          />
                        )}
                        <>
                          {disableForm && !isAccessAllowed && (
                            <div
                              className="py-1 text-sm bg-gray-200 items-center text-red-500 leading-none lg:rounded-full flex lg:inline-flex"
                              role="alert"
                            >
                              <span className="font-semibold px-2 text-left flex-auto">
                                To activate the form fields please verify your
                                ID first
                              </span>
                            </div>
                          )}
                          <Field
                            name="names"
                            component={NamesFormEditable}
                            idType={row.original.idType}
                            designation={row.original.designation}
                            disabled={disableForm}
                          />
                          <hr />
                          <IdentificationDetailsForm
                            idType={
                              nationalityChangeForm
                                ? values.idType
                                : row.original.idType
                            }
                            designation={row.original.designation}
                            setNationalityChangeForm={setNationalityChangeForm}
                            setDisableForm={setDisableForm}
                            disabled={disableForm}
                          />
                          <hr />
                          <h3 className="text-lg mb-2 font-medium leading-6 text-gray-900">
                            Effective Dates
                          </h3>
                          <div className="grid grid-cols-6 gap-6">
                            <Field
                              name="appointmentDate"
                              component={ManageAppointmentDate}
                              idType={row.original.idType}
                              designation={row.original.designation}
                              disabled
                            />
                            <Field
                              name="changeOfParticularDate"
                              component={ManageDateOfChange}
                              idType={row.original.idType}
                              designation={row.original.designation}
                              disabled={disableForm}
                            />
                          </div>
                        </>
                      </div>
                    </div>
                  )}
                  {selectedTab.id === "residentialDetails" && (
                    <ResidentialDetails
                      idType={official?.idType}
                      designation={official?.designation}
                      errors={errors}
                      touched={touched}
                      isNonResident={official?.isNonResident}
                    />
                  )}
                  {selectedTab.id === "fileUploads" && (
                    <div className="mt-4">
                      <Field
                        name="fileUploads"
                        component={FileUploadForm}
                        documentData={getDocumentData(official?.idType)}
                        disabled={false}
                      />
                    </div>
                  )}
                  {selectedTab.id === "changedDocuments" && (
                    <div className="mt-4">
                      {changedFields?.names && (
                        <Field
                          name="changeDocumentsType"
                          component={DocumentsUpload}
                          idType={row.original.idType}
                          designation={row.original.designation}
                          changedFields={changedFields}
                        />
                      )}
                      {changedFields?.electronicAddress &&
                        Object.values(
                          changedFields.electronicAddress || {}
                        ).some(Boolean) &&
                        Constants.INDIVIDUALS_ID_TYPES.includes(
                          row.original.idType
                        ) && (
                          <Field
                            name="statutoryDeclaration"
                            component={DocumentsUpload}
                            idType={row.original.idType}
                            designation={row.original.designation}
                            changedFields={changedFields}
                          />
                        )}
                    </div>
                  )}
                  {errors && (
                    <ErrorBanner
                      errors={Object.values(errors).flatMap((value) =>
                        typeof value === "object" ? Object.values(value) : value
                      )}
                      className="my-4"
                    />
                  )}
                  <div className="flex justify-between">
                    {!isFirstStep && (
                      <button
                        onClick={goToPrevStep}
                        className="flex items-start float-right py-2 font-semibold rounded text-primary mt-7"
                        type="button"
                      >
                        <span className="flex items-center mr-4 h-7">
                          <ChevronLeftIcon
                            className="flex-shrink-0 w-5 h-5"
                            aria-hidden="true"
                          />
                        </span>
                        <p className="font-semibold">Go back</p>
                      </button>
                    )}
                    {isLastStep ? (
                      <Button
                        type="button"
                        onClick={submitForm}
                        isLoading={isSubmitting}
                        className="flex items-center px-4 py-2 mb-2 font-semibold text-white rounded mt-7 disabled:cursor-not-allowed"
                      >
                        Save and proceed
                      </Button>
                    ) : (
                      <Button
                        onClick={goToNextStep}
                        className="flex items-start float-right py-2 ml-auto font-semibold rounded mt-7 mb-4"
                        type="button"
                      >
                        Next
                      </Button>
                    )}
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </td>
    </tr>
  );
};

export default ChangeOfParticularsSubRow;
